import React, { createContext, useContext, useState, useEffect } from 'react';
import { safeFetch } from '../config/api';

const B2BAuthContext = createContext(null);

export const useB2BAuth = () => {
  const context = useContext(B2BAuthContext);
  if (!context) {
    throw new Error('useB2BAuth must be used within B2BAuthProvider');
  }
  return context;
};

export const B2BAuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem('b2b_user');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(() => localStorage.getItem('b2b_token') || null);

  useEffect(() => {
    // Token already loaded synchronously from localStorage initializer
    // This effect only needed if we want to verify token validity later
  }, []);

  const login = async (email, password) => {
    try {
      const response = await safeFetch('/api/b2b/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      // Read body ONCE as text, then try to parse JSON. Prevents the
      // "body stream already read" class of bugs if an upstream layer
      // returns non-JSON (e.g. HTML 502 page).
      const raw = await response.text();
      let data = {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {
        data = { detail: raw || 'Unexpected server response' };
      }

      if (!response.ok) {
        throw new Error(data.detail || `Login failed (HTTP ${response.status})`);
      }

      setToken(data.access_token);
      setUser(data.user);

      localStorage.setItem('b2b_token', data.access_token);
      localStorage.setItem('b2b_user', JSON.stringify(data.user));

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('b2b_token');
    localStorage.removeItem('b2b_user');
  };

  const isAdmin = () => user?.role === 'admin';
  const isPartner = () => user?.role === 'partner';
  const isViewer = () => user?.role === 'viewer';
  // Anyone on the internal team — can see invoices, deals, etc.
  const isInternalStaff = () =>
    user?.role === 'admin' || user?.role === 'admin_assistant';

  const authFetch = async (url, options = {}) => {
    if (!token) {
      console.error('authFetch: No token available');
      throw new Error('Not authenticated');
    }

    console.log('authFetch:', options.method || 'GET', url);
    
    const response = await safeFetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    console.log('authFetch response:', response.status, response.statusText);

    if (response.status === 401) {
      logout();
      throw new Error('Session expired');
    }

    return response;
  };

  const updateUser = (patch) => {
    setUser((prev) => {
      const next = { ...(prev || {}), ...(patch || {}) };
      try { localStorage.setItem('b2b_user', JSON.stringify(next)); } catch { /* */ }
      return next;
    });
  };

  return (
    <B2BAuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      logout,
      updateUser,
      isAdmin,
      isPartner,
      isViewer,
      isInternalStaff,
      authFetch,
      isAuthenticated: !!token
    }}>
      {children}
    </B2BAuthContext.Provider>
  );
};

export default B2BAuthProvider;
